import { gql, useQuery } from '@apollo/client';
import React from 'react';
import Button from '../components/Button';
import ExternalBrandLogo from '../components/ExternalBrandLogo';
import SVGIcon from '../components/SVGIcon';
import clearIcon from '../icons/clear.svg';
import { SEARCH_PLATFORM_INFO_MAP } from '../integrations/IntegrationPlatformInfo';
import UIActions from '../ui/UIActions';
import { useLocation, useRouter } from '../utils/router/v3Bridge';
import SearchPlatformTab from './SearchPlatformTab';
import styles from './SearchPlatformExternalPlatformTab.module.scss';
import { useAppDispatch } from '../app/redux/useAppSelector';

const RESULT_COUNT_QUERY = gql`
  query SearchPlatformExternalPlatformTab($platform: String!, $query: String!) {
    externalSearch(platform: $platform, query: $query) {
      id
    }
  }
`;

interface SearchPlatformExternalPlatformTabProps {
  active: boolean;
  encourageInstallation: boolean;
  platformKey: string;
  searchQuery: string;
  className?: string;
  activeClassName?: string;
  iconHeight?: number;
  vertical?: boolean;
}

const SearchPlatformExternalPlatformTab = ({
  active,
  encourageInstallation,
  platformKey,
  searchQuery,
  className,
  activeClassName,
  iconHeight = 20,
  vertical,
}: SearchPlatformExternalPlatformTabProps) => {
  const dispatch = useAppDispatch();
  const platformInfo = SEARCH_PLATFORM_INFO_MAP[platformKey];
  const router = useRouter();
  const location = useLocation();

  const { data, loading, error } = useQuery(RESULT_COUNT_QUERY, {
    variables: { platform: platformKey, query: searchQuery },
  });

  if (!platformInfo) return null;
  const { name, icon } = platformInfo;

  const handleDismiss = (platform: string) => {
    dispatch(UIActions.dismissSearchConnectionPrompt(platform));
    if (active) {
      router.push({
        ...location,
        query: { ...location.query, platform: undefined },
      });
    }
  };

  return (
    <SearchPlatformTab
      key={platformKey}
      platform={platformKey}
      className={className}
      activeClassName={activeClassName}
      icon={icon && <ExternalBrandLogo {...icon} height={iconHeight} />}
      name={name === 'Google Drive' ? 'Drive' : name}
      meta={
        !loading &&
        (encourageInstallation ? (
          <Button
            className={styles.dismissButton}
            onClick={(e) => {
              e.preventDefault();
              handleDismiss(platformKey);
            }}
          >
            <SVGIcon size={10}>{clearIcon}</SVGIcon>
          </Button>
        ) : (
          <>{error ? null : data?.externalSearch.length ?? 0}</>
        ))
      }
      vertical={vertical}
    />
  );
};

export default SearchPlatformExternalPlatformTab;
